import {
  CircularProgress,
  DialogTitle,
  Divider,
  Input,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Box,
} from "@mui/material";
import {
  CloseIconWrapper,
  CreateBoardWrapper,
  FontGrayNormal,
  ModalIconWrapper,
  ModalTitle,
  StyledContainedButton,
  StyledDialog,
  StyledFormControl,
  StyledInputLabel,
  StyledTextField,
} from "./style";
import {
  CloseIcon,
  CreateFolderIcon,
  UploadImageIcon,
} from "../../assets/Icons";
import { CreateTeamValueProps } from "../../utils/propTypes";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { createTeamValidationSchema } from "../../utils/formValidationSchema";
import { ErrorMessage } from "../../pageComponents/auth/style";
import {
  membersListSelector,
  teamDetailsByIdSelector,
  teamDetailsLoaderSelector,
} from "../../store/metadata/selector";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  LogoUploadButtonAfter,
  SquareContainer,
  UploadedImage,
} from "../../pageComponents/settings/style";
import { useTheme } from "styled-components";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import type { Member } from "../../utils/propTypes";
import { userDetailsSelector } from "../../store/account/selector";

const EditTeamModal = ({
  isOpen,
  setIsOpen,
  teamId,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  teamId: string;
}): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const profileImageInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [logoErrorMessage, setLogoErrorMessage] = useState<string>("");
  const [teamMembers, setTeamMembers] = useState<Member[]>([]);
  const membersList = useSelector(membersListSelector);
  const teamDetails = useSelector(teamDetailsByIdSelector);
  const loading = useSelector(teamDetailsLoaderSelector);
  const userDetails = useSelector(userDetailsSelector);

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
  } = useForm<CreateTeamValueProps>({
    resolver: yupResolver(createTeamValidationSchema),
    defaultValues: {
      name: "",
      members: [],
    },
  });

  // Fetch team details and members list when modal opens
  useEffect(() => {
    if (isOpen && teamId && teamDetails?._id !== teamId) {
      dispatch(MetaDataSagaActions.getInviteMembersList());
      dispatch(MetaDataSagaActions.getTeamDetails({ teamId }));
    }
  }, [isOpen, teamId, teamDetails?._id]);

  // Update form state when team details are loaded
  useEffect(() => {
    if (teamDetails && teamDetails?._id === teamId) {
      const memberOptions =
        membersList?.members
          ?.filter(
            (member: any) =>
              member?.acceptedStatus === "ACCEPTED" &&
              member?._id !== userDetails?._id
          )
          ?.map((member: any) => ({
            label: member.fullName,
            id: member._id,
            email: member.email,
          })) || [];

      setTeamMembers(memberOptions);

      const selectedInitialMembers =
        teamDetails?.members
          ?.filter((member: any) => !member?.isOwner)
          ?.map((member: any) => ({
            label: member.fullName,
            id: member._id,
            email: member.email,
          })) || [];

      reset({
        name: teamDetails.name,
        members: selectedInitialMembers,
      });
    }
  }, [teamDetails, teamId, membersList, reset]);

  // Cleanup object URL when file changes
  useEffect(() => {
    let objectUrl: string | undefined;

    if (selectedFile) {
      objectUrl = URL.createObjectURL(selectedFile);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const handleCloseModal = () => {
    setIsOpen(false);
    setLogoErrorMessage("");
    setSelectedFile(null);
    setTeamMembers([]);
    reset();
    dispatch(MetaDataReducerAction.setTeamDetailsById(null));
  };

  const handleProfileImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        setLogoErrorMessage("Only PNG, JPG, and JPEG formats are supported.");
        setSelectedFile(null);
        if (profileImageInputRef.current) {
          profileImageInputRef.current.value = ""; // reset file input
        }
        return;
      }

      // ✅ valid file
      setSelectedFile(file);
      setLogoErrorMessage("");
    }
  };

  const handleProfileUploadButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    profileImageInputRef.current?.click();
  };

  const onSubmit = (data: CreateTeamValueProps) => {
    if (loading) return;

    const formattedMembers = data?.members?.map((item) => ({
      email: item?.email,
      accepted: true,
      isOwner: false,
    }));

    dispatch(
      MetaDataSagaActions.updateTeam({
        teamId,
        name: data.name,
        logo: selectedFile || undefined,
        members: formattedMembers,
        handleCloseModal,
      })
    );
  };

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ padding: "1.875rem 1.875rem 1.25rem 1.875rem" }}>
        <Stack direction="row" gap="0.75rem">
          <ModalIconWrapper>
            <CreateFolderIcon fill="white" />
          </ModalIconWrapper>
          <Stack>
            <ModalTitle>Edit Team</ModalTitle>
            <FontGrayNormal>
              Update a team name, logo, and update members to get started.
            </FontGrayNormal>
          </Stack>
          <CloseIconWrapper aria-label="close" onClick={handleCloseModal}>
            <CloseIcon />
          </CloseIconWrapper>
        </Stack>
      </DialogTitle>

      <Divider sx={{ borderColor: "#E3E6EA" }} />

      <CreateBoardWrapper>
        {/* Team Name */}
        <StyledFormControl fullWidth>
          <StyledInputLabel htmlFor="name">Team Name</StyledInputLabel>
          <StyledTextField id="name" {...register("name")} />
          {errors.name?.message && (
            <ErrorMessage>{errors.name.message}</ErrorMessage>
          )}
        </StyledFormControl>

        {/* Team Members */}
        <StyledFormControl fullWidth>
          <StyledInputLabel>Select Team Members</StyledInputLabel>
          <Controller
            name="members"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={teamMembers}
                // ✅ show both name + email
                getOptionLabel={(option) =>
                  option?.label && option?.email
                    ? `${option.label} (${option.email})`
                    : option?.label || ""
                }
                isOptionEqualToValue={(opt, val) => opt?.email === val?.email}
                onChange={(_, value) => field?.onChange(value)}
                value={field?.value || []}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography fontWeight={500}>{option?.label}</Typography>
                      <Typography
                        fontSize="0.875rem"
                        color={theme.colors.gray1}
                      >
                        {option?.email}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      // ✅ show both name + email in chip
                      label={`${option?.label} (${option?.email})`}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select member"
                  />
                )}
              />
            )}
          />
          {errors.members?.message && (
            <ErrorMessage>{errors.members.message}</ErrorMessage>
          )}
        </StyledFormControl>

        {/* Logo Upload */}
        <StyledFormControl fullWidth>
          <Typography sx={{ color: theme.colors.black2, fontSize: "1.125rem" }}>
            Upload Image
          </Typography>
          <SquareContainer>
            <Input
              type="file"
              style={{ display: "none" }}
              inputRef={(el) => (profileImageInputRef.current = el)}
              onChange={handleProfileImageSelect}
              inputProps={{ accept: "image/*" }}
            />
            <LogoUploadButtonAfter onClick={handleProfileUploadButtonClick}>
              <UploadImageIcon />
            </LogoUploadButtonAfter>

            {(selectedFile || teamDetails?.logo) && (
              <UploadedImage
                src={
                  selectedFile
                    ? URL.createObjectURL(selectedFile)
                    : (teamDetails?.logo as string)
                }
                alt="Uploaded Logo"
              />
            )}
          </SquareContainer>

          {logoErrorMessage && <ErrorMessage>{logoErrorMessage}</ErrorMessage>}
        </StyledFormControl>
      </CreateBoardWrapper>

      <Divider sx={{ borderColor: "#E3E6EA" }} />

      <StyledContainedButton
        variant="contained"
        size="small"
        sx={{ m: "1.875rem 1.875rem 1.875rem auto", px: "2.375rem" }}
        onClick={handleSubmit(onSubmit)}
        disabled={loading}
        startIcon={loading && <CircularProgress size={20} color="inherit" />}
      >
        Update Team
      </StyledContainedButton>
    </StyledDialog>
  );
};

export default EditTeamModal;
